import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { wrappedTranslations } from '../../../translations/wrapped';

interface EloJourneyCardProps {
    eloHistory: Array<{ date: string; elo: number }>;
    currentElo: number;
    peakElo: number;
    lowestElo: number;
    eloChange: number;
    lang: 'en' | 'fr' | 'de' | 'it';
}

const EloJourneyCard: React.FC<EloJourneyCardProps> = ({
    eloHistory,
    currentElo,
    peakElo,
    lowestElo,
    eloChange,
    lang,
}) => {
    const t = wrappedTranslations[lang];
    const chartData = eloHistory
        .filter(entry => entry.date)
        .map(entry => ({
            date: entry.date,
            elo: entry.elo,
        }))
        .sort((a, b) => {
            const dateA = new Date(a.date.split('.').reverse().join('-'));
            const dateB = new Date(b.date.split('.').reverse().join('-'));
            return dateA.getTime() - dateB.getTime();
        });

    return (
        <div className="h-full w-full flex flex-col items-center justify-center text-center p-6">
            <div className="max-w-5xl w-full flex flex-col items-center">
                <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring" }}
                >
                    <TrendingUp className="w-20 h-20 md:w-24 md:h-24 mb-6 mx-auto text-[#1C7AD6] drop-shadow-[0_0_15px_rgba(28,122,214,0.4)]" />
                </motion.div>

                <motion.h2
                    className="text-2xl md:text-4xl font-bold mb-4 text-gray-900 tracking-tight"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                >
                    {t.yourEloJourney}
                </motion.h2>

                <motion.p
                    className="text-base md:text-xl text-gray-600 mb-6 md:mb-10 font-light"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                >
                    {eloChange >= 0 ? t.upwardTrajectory : t.buildingCharacter}
                </motion.p>

                <motion.div
                    className="w-full h-48 md:h-96 mb-6 md:mb-10 bg-white border border-gray-200 rounded-2xl p-6 md:p-8 shadow-lg"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                >
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData}>
                            <defs>
                                <linearGradient id="colorElo" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#1C7AD6" stopOpacity={0.2} />
                                    <stop offset="95%" stopColor="#1C7AD6" stopOpacity={0.05} />
                                </linearGradient>
                            </defs>
                            <Area
                                type="monotone"
                                dataKey="elo"
                                stroke="#1C7AD6"
                                strokeWidth={4}
                                fill="url(#colorElo)"
                                animationDuration={2000}
                            />
                            <YAxis hide domain={['dataMin - 20', 'dataMax + 20']} />
                            <XAxis hide />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: '#ffffff',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '12px',
                                    color: '#111827',
                                    padding: '12px',
                                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                                }}
                                itemStyle={{ color: '#1C7AD6', fontWeight: 'bold' }}
                                labelStyle={{ color: '#6b7280' }}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </motion.div>

                <div className="grid grid-cols-3 gap-2 md:gap-8 w-full">
                    <motion.div
                        className="bg-white border border-gray-200 rounded-2xl p-3 md:p-6 flex flex-col items-center justify-center shadow-md"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.8 }}
                    >
                        <div className="text-3xl md:text-5xl font-bold text-gray-900 mb-1 md:mb-2">{currentElo}</div>
                        <div className="text-[10px] md:text-xs text-gray-600 uppercase tracking-wider font-semibold">{t.current}</div>
                    </motion.div>
                    <motion.div
                        className="bg-white border border-gray-200 rounded-2xl p-3 md:p-6 flex flex-col items-center justify-center shadow-md"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.9 }}
                    >
                        <div className="text-3xl md:text-5xl font-bold text-emerald-600 mb-1 md:mb-2">{peakElo}</div>
                        <div className="text-[10px] md:text-xs text-gray-600 uppercase tracking-wider font-semibold">{t.peak}</div>
                    </motion.div>
                    <motion.div
                        className="bg-white border border-gray-200 rounded-2xl p-3 md:p-6 flex flex-col items-center justify-center shadow-md"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.0 }}
                    >
                        <div className="text-3xl md:text-5xl font-bold text-gray-600 mb-1 md:mb-2">{lowestElo}</div>
                        <div className="text-[10px] md:text-xs text-gray-500 uppercase tracking-wider font-semibold">{t.lowest}</div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

export default EloJourneyCard;
